/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './ChargingSessionTrend.css';
import { IconWithTooltip } from '../../globalUtils/globalIcons';
import { useAppDispatch, useAppSelector } from '../../rmsReduxStore/rmsReduxHooks';
import { getCurrentFiltersValues, getCurrentScreenAppliedFiltersCount, getDashboardReducer, getFilterSelectedValue, getReferenceDataReducer, setFilterData } from '../../rmsReduxStore/reduxExports';
import { SelectDropDown, TableSubHeader, useEffectOnce } from '../../globalUtils/globalExports';
import { formatNumberWithCommas, getValueInSeconds, renderHeaderActionButtons } from '../../globalUtils/globalHooks';
import { RectangularSkeleton } from '../../globalUtils/SkeletonDesign/SkeletonDesign';
import CustomFilterLayout, { CustomFilterProp } from './CustomFilterLayout';

interface ChargingSessionTrendProp {
  handleFilter: Function,
  getFilterValue: Function
}

const ChargingSessionTrend: React.FC<ChargingSessionTrendProp> = (props) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { handleFilter, getFilterValue } = props;
  const { intervalRangeReferenceData,chargingStationReferenceData,chargerVisibleIdReferenceData } = useAppSelector(getReferenceDataReducer);
  const { newDashboardChargingSessionTrendInfo } = useAppSelector(getDashboardReducer);
  // const [filterValue, setFilterValue] = useState<IGeneralReferenceData>()
  const dispatch = useAppDispatch();
  const navigationUpperLimit = 30 //draft
  const [visibleMin, setVisibleMin] = useState(0);
  const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
  const chartHeight = 400;
  const [filterAnchorEl,setFilterAnchorEl] = useState<HTMLElement | null>(null);

  // useEffectOnce(() => {
  //   setFilterValue(getFilterValue('sessionChargingIntervalRange'))
  // })

  const getFormattedData = useCallback(() => {  //draft
    const data = newDashboardChargingSessionTrendInfo || [];
    const slicedData = data.slice(visibleMin, visibleMax + 1);
  
    const xAxis = slicedData.map(item => item?.date);
    const yAxis = slicedData.map(item => (item?.data) / 60);
  
    const trendline = yAxis.map((_, idx) => {
      const idx6 = idx - 3 > 0 ? idx-3 : 0;
      const idx1 = idx - 2 > 0 ? idx-2 : 0;
      const idx2 = idx - 1 > 0 ? idx-1 : 0;
      const idx3 = idx;
      const idx4 = idx + 1 < yAxis.length ? idx+1 : yAxis.length - 1;
      const idx5 = idx + 2 < yAxis.length ? idx+2 : yAxis.length - 1;
      const idx7 = idx + 3 < yAxis.length ? idx+3 : yAxis.length - 1;
      // const sum = yAxis[idx1] + yAxis[idx2] + yAxis[idx3] + yAxis[idx4] + yAxis[idx5];
      // return +(sum / 5).toFixed(2);

      const sum = yAxis[idx1] + yAxis[idx2] + yAxis[idx3] + yAxis[idx4] + yAxis[idx5] + yAxis[idx6] + yAxis[idx7];
      return +(sum / 7).toFixed(2);
    });
  
    return { xAxis, yAxis, trendline };
  }, [newDashboardChargingSessionTrendInfo, visibleMin, visibleMax]);  
  
  getFormattedData();

  const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const rawValue = this.value;
    const label = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
    const [day, month] = label.split(' ');
  
    const chartWidth = this.axis.chart.chartWidth;
    const numCategories = this.axis.categories?.length || 1;
    const approxCategoryWidth = chartWidth / numCategories;
  
    const shouldWrap = approxCategoryWidth > 0;
  
    return shouldWrap
      ? `<div style="text-align: center;">${day}<br>${month}</div>`
      : `<div style="text-align: center;">${day}<br><span style="opacity: 0.6; font-size: 10px;">${month}</span></div>`;
  };  

  const formatDuration = (seconds: number,shortAbb = true) => {
    if (seconds === 0) return shortAbb ? '0 s' : '0 Sec';
  
    const units = [
      { label: 'Hour',shortAbb: 'h',  value: Math.floor((seconds) / 3600) },
      { label: 'Min',shortAbb: 'm',   value: Math.floor((seconds % 3600) / 60) },
      { label: 'Sec', shortAbb: 's',  value: Math.floor(seconds % 60) }
    ];

    const nonZeroUnits = units.filter(unit => unit.value > 0).slice(0, 2);

    return nonZeroUnits
      .map(unit => `${unit.label == 'Hour' ? formatNumberWithCommas(unit.value || 0):unit.value}${shortAbb? unit?.shortAbb : (' ' + unit.label)}${(unit.value) > 1 && !shortAbb ? 's' : ''}`)
      .join(' ');
  }

  const formatTimeData = (val: number) => {
    const valInSeconds = getValueInSeconds(val, 'h');
    const formattedData = formatDuration(valInSeconds);
    return formattedData;
  }

  let labelStep = 1;  //draft
  const { xAxis } = getFormattedData();
  const totalBars = xAxis.length;
  if (totalBars > 20) labelStep = 4;
  else if (totalBars > 10) labelStep = 2;

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: chartHeight,
      spacing: [10, 20, 0, 10],
      events: {
        render: function () {
          const chart = this as Highcharts.Chart & any;
  
          // Remove existing custom arrows before adding new ones
          const existingArrows = chart.container.getElementsByClassName('custom-arrow');
          while (existingArrows.length > 0) {
            existingArrows[0].remove();
          }
  
          // Left arrow (shifted more inside by changing left position)
          if (visibleMin > 0) {
            chart.renderer.image(
              '/images/left_arrow_highchart.svg',
              chart.plotLeft - 12, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow left')
            .on('click', () => handleNavigation('left'))
            .add();
          }
  
          // Right arrow (shifted more inside by changing left position)
          if (visibleMax < dataLength - 1) {
            chart.renderer.image(
              '/images/right_arrow_highchart.svg',
              chart.plotLeft + chart.plotWidth - 10, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
            .addClass('custom-arrow right')
            .on('click', () => handleNavigation('right'))
            .add();
          }
        }
      },
    },
    title: { text: undefined },
    subtitle: { text: undefined },
    xAxis: {
      categories: getFormattedData()?.xAxis,
      lineColor: '#005EFF',
      tickColor: '#005EFF',
      gridLineWidth: (newDashboardChargingSessionTrendInfo?.length ?? 0) > 10 ? 0 : 1,
      gridLineDashStyle: 'ShortDash', 
      plotBands: getFormattedData().xAxis.map((_, index) => {
        const barWidthFraction = 0.25;
        return {
          from: index - barWidthFraction,
          to: index + barWidthFraction,
          color: '#f5f5f5'
        };
      }), 
      labels: {
        rotation: 0,
        step: labelStep,  //draft
        formatter: xAxisLabelFormatter
      }
    },
    yAxis: {
      title: { text: 'Hours' },
      gridLineDashStyle: 'ShortDash',
      // tickInterval: 6
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const points = this.points || [];
        const category = this.x;

        return `
          ${category}
          ${points.map(point => {
            const name = point.series.name;
            const value = formatTimeData(point.y || 0);
            const color = point.color;
            return `<br/><span style="color:${color}">\u25CF</span> ${name}: <b>${value}</b>`;
          }).join('')}
        `;
      }
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      enabled: true,
      useHTML: true,
      symbolWidth: 0,
      labelFormatter: function () {
        const isVisible = this.visible !== false;
        const textStyle = isVisible ? '' : 'color: #999; text-decoration: line-through;';
        const boxColor = isVisible ? this.color : '#ccc';
    
        return `
          <span style="display: inline-flex; align-items: center; gap: 6px; ${textStyle}">
            <span style="width: 10px; height: 10px; background-color: ${boxColor}; display: inline-block; border-radius: 50%;"></span>
            <span>${this.name}</span>
          </span>
        `;
      },
      itemStyle: {
        fontSize: '12px',
        cursor: 'default',
      },
      symbolHeight: 0,
      symbolPadding: 0,
      symbolRadius: 0
    },
    
    plotOptions: {
      column: {
        borderRadius: 8,
        pointPadding: 0.2,
        groupPadding: 0.1,
      },
      series: {
        marker: {
          enabled: false,
        },
      },
    },
    series: [
      {
        name: 'Charging Session',
        type: 'column',
        data: getFormattedData().yAxis,
        color: 'var(--secondary-blue-color)'
      },
      // {
      //   name: 'Trendline',
      //   type: 'line',
      //   data: getFormattedData().trendline, //draft
      //   color: '#D21616',
      //   lineWidth: 2,
      //   marker: {
      //     enabled: false,
      //   },
      // }
    ],
    credits: { enabled: false },
    exporting: { enabled: false },
  };


  const dataLength = newDashboardChargingSessionTrendInfo?.length || 0;

  const handleNavigation = (direction: 'left' | 'right') => {
    const step = navigationUpperLimit + 1;
    if (direction === 'left') {
        setVisibleMin(Math.max(visibleMin - step, 0));
        setVisibleMax(Math.max(visibleMax - step, step));
    } else {
        setVisibleMin(Math.min(visibleMin + step, dataLength - (step + 1)));
        setVisibleMax(Math.min(visibleMax + step, dataLength - 1));
    }
};

  // const handleChargingSessionFilter = useCallback(async (val: any, filterKey: string): Promise<void> => {
  //   const res = await setFilterData(val, filterKey, 'dashboardV2', 'SET', false)(dispatch)
  //   if (res?.message === 'Action dispatched successfully') {
  //       handleFilter(res?.filters);
  //   }
  // }, [setFilterData])

  // const handleSelectChange = (val): void => {
  //   const cardValue = val?.label === 'All' ? 'TOTAL' : val?.value?.toString()?.toLocaleUpperCase()
  //   setFilterValue(val);
  //   handleChargingSessionFilter(val, 'sessionChargingIntervalRange');
  //   handleChartPageReset();
  // };

  const handleChartPageReset = () => {
    setVisibleMin(0);
    setVisibleMax(navigationUpperLimit);
  }

   const headerActionBtns = renderHeaderActionButtons({
      actionBtns:
          [
              {
                  id: 'add_filter_btn',
                  isAccessible: true,
                  headerActionBtnClick: (e): void => {
                      setFilterAnchorEl(e.currentTarget)
                  },
                  badgeCount: getCurrentScreenAppliedFiltersCount('chargingSessionTrend',true)
              },
          ],
    })
  
    const handleFilterDataChange = async (val, filterKey, isGlobal) => {
        const res = await setFilterData(val, filterKey, 'chargingSessionTrend', 'SET', isGlobal ? isGlobal : false,true)(dispatch)
        const dashboardV2Filters = getCurrentFiltersValues('dashboardV2')
        if (res?.message === 'Action dispatched successfully') {
          handleFilter(dashboardV2Filters,res?.filters)
          handleChartPageReset();
        }
    }
  
    const chargingSessionTrendFilterProps: CustomFilterProp[] = useMemo(()=>{
      return [
        {
          filterLabel: 'Charging Stations',
          filterType: 'dropdown',
          filterId: 'chargingStationId',
          filterDropdownProps: {
            selectValue: getFilterSelectedValue('chargingStationId', 'chargingSessionTrend', false),
            selectOptions: chargingStationReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargingStationId', false)
            },
            selectDropDownId: 'charging-session-trend-chargingStationId-type-filter-dropdown',
            isMultiSelect: true,
            renderToBody:true,
          }
        },
        {
          filterLabel: 'Chargers',
          filterId: 'chargerId',
          filterType: 'dropdown',
          filterDropdownProps: {
            selectValue: getFilterSelectedValue('chargerId', 'chargingSessionTrend', false),
            selectOptions: chargerVisibleIdReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargerId', false)
            },
            selectDropDownId: 'charging-session-trend-charger-id-filter-dropdown',
            isMultiSelect: true,
            renderToBody: true
          }
        },
        {
          filterLabel: 'Date Range',
          filterId: 'sessionChargingIntervalRange',
          filterType: 'dropdown',
          filterDropdownProps: {
            selectValue: getFilterSelectedValue('sessionChargingIntervalRange', 'chargingSessionTrend', false),
            selectOptions: intervalRangeReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'sessionChargingIntervalRange', false)
            },
            selectDropDownId: 'charging-session-trend-date-range-filter-dropdown',
            isMultiSelect: false,
            renderToBody: true
          }
        },
      ]
    },[intervalRangeReferenceData,chargerVisibleIdReferenceData,chargingStationReferenceData,handleFilterDataChange,getFilterSelectedValue])

  return (
    <div className="widget-wrap charging-session-trend-wrap">
      <div className="widget-header chart-header">
        <div className='widget-heading charging-session-trend-header'>
          Charging Session Trend
          <IconWithTooltip iconClassName='info-icon' toolTipText='Trend analysis based on charging stations' containerClassName={'new-dashboard-widget-header-tooltip'} />
        </div>
        <div className="chart-filters">

          {/* <SelectDropDown
           selectValue={filterValue}
           selectOptions={intervalRangeReferenceData ?? []}
           handleSelectOptionChange={handleSelectChange}
           selectDropDownId='alarm-failed-transaction-dropdown'
           dropDownClassName='alarm-failed-transaction-dropdown'
           isMultiSelect={false}
           placeholder={'Interval'}
          /> */}
          <TableSubHeader headerActionBtns={headerActionBtns}/>
          <CustomFilterLayout filters={chargingSessionTrendFilterProps} requiredReferenceDataCallbacks={['chargerVisibleId','chargingStationId']} filterAnchorEl={filterAnchorEl} setFilterAnchorEl={setFilterAnchorEl} excludeGlobalFilters={true} />
        </div>
      </div>
<div className='widget-body utilization-chart-body'>
  {!newDashboardChargingSessionTrendInfo ? (
    <div style={{ height: `${chartHeight}px` }}>
      <RectangularSkeleton skeletonContainerClassName='' />
    </div>
  ) : newDashboardChargingSessionTrendInfo?.length === 0 ? (
    <div className='no-data-text'>
      No data available
    </div>
  ) : (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  )}
</div>

    </div>
  );
};

export default ChargingSessionTrend;